/**
 * Canonical React Query key factory for SWIM storefront.
 *
 * All query keys live here. Never inline string arrays in useQuery calls.
 * Same key = same cache = same data across all component instances.
 *
 * @example
 * useQuery({ queryKey: QUERY_KEYS.drops.balance(memberId), queryFn: ... })
 * useQuery({ queryKey: QUERY_KEYS.offers.detail(offerId), queryFn: ... })
 */
export const QUERY_KEYS = {
  drops: {
    /** Member's current drop balance */
    balance: (memberId: string) => ['drops', 'balance', memberId] as const,
    /** Member's transaction history */
    transactions: (memberId: string) => ['drops', 'transactions', memberId] as const,
  },
  offers: {
    /** All published offers (browse page) */
    list: (filters?: Record<string, unknown>) => ['offers', 'list', filters ?? {}] as const,
    /** Infinite paginated offer list for browse page — useInfiniteQuery */
    infinite: (filters?: Record<string, unknown>) => ['offers', 'infinite', filters ?? {}] as const,
    /** Featured published offers — homepage grid (6 max) */
    featured: () => ['offers', 'featured'] as const,
    /** Featured offers for homepage masonry grid (50 max) */
    featuredGrid: () => ['offers', 'featured-grid'] as const,
    /** Single offer detail */
    detail: (offerId: string) => ['offers', 'detail', offerId] as const,
    /** Merchant's own offers (merchant portal) */
    byMerchant: (merchantId: string) => ['offers', 'merchant', merchantId] as const,
  },
  streams: {
    /** All streams list (discovery page) */
    list: () => ['streams', 'list'] as const,
    /** Stream detail by slug */
    detail: (slug: string) => ['streams', 'detail', slug] as const,
    /** Stream Policies tab content by slug — separate fetch, lazy-loaded on tab view */
    policies: (slug: string) => ['streams', 'policies', slug] as const,
    /** Paginated offers for a stream — useInfiniteQuery */
    offers: (slug: string, filters?: Record<string, unknown>) =>
      ['streams', 'offers', slug, filters ?? {}] as const,
  },
  claims: {
    /** Member's claims */
    byMember: (memberId: string) => ['claims', 'member', memberId] as const,
    /** Single claim */
    detail: (claimId: string) => ['claims', 'detail', claimId] as const,
    /** Merchant's incoming claims */
    byMerchant: (merchantId: string) => ['claims', 'merchant', merchantId] as const,
    /** Claims for a specific offer */
    byOffer: (offerId: string) => ['claims', 'offer', offerId] as const,
    /** All claims (member's history) */
    list: () => ['claims', 'list'] as const,
    /** Buyer's own download access log for a claim */
    accessLog: (claimId: string) => ['claims', 'access-log', claimId] as const,
  },
  account: {
    /** Current authenticated member */
    me: () => ['account', 'me'] as const,
    /** Authenticated member's own profile (handle/bio/avatar) — session-derived, no id needed */
    profile: () => ['account', 'profile'] as const,
    /** Authenticated merchant's own streams */
    myStreams: () => ['account', 'my-streams'] as const,
  },
  merchant: {
    /** Merchant storefront config (slug → theme, accent, font) */
    storefront: (slug: string) => ['merchant', 'storefront', slug] as const,
    /** Merchant profile */
    profile: (merchantId: string) => ['merchant', 'profile', merchantId] as const,
  },
  categories: {
    /** All top-level categories with children */
    list: () => ['categories', 'list'] as const,
    /** Paginated offers for a category slug */
    offers: (slug: string) => ['categories', 'offers', slug] as const,
    /** Paginated streams for a category slug */
    streams: (slug: string) => ['categories', 'streams', slug] as const,
  },
  disputes: {
    /** Dispute for a claim (if any) */
    byClaimId: (claimId: string) => ['disputes', 'byClaimId', claimId] as const,
    /** Messages in a dispute thread */
    messages: (disputeId: string) => ['disputes', 'messages', disputeId] as const,
  },
  dataRights: {
    /** Latest data export request status (spec 010) */
    exportStatus: () => ['data-rights', 'export-status', 'consumer'] as const,
  },
  search: {
    /** List of search results — useInfiniteQuery */
    infinite: (filters?: Record<string, unknown>) => ['search', 'infinite', filters ?? {}] as const,
    /** Search suggestions (cmdk dropdown) */
    suggestions: (q: string) => ['search', 'suggestions', q] as const,
  },
} as const
